import { AxiosResponse } from 'axios';
import { IntlFormatters } from 'react-intl';
import { IValidationApiResponse } from '../../interfaces/apiResponse';
import { URL_REMINDERS, URL_REMINDERS_LOGS, URL_SENT_REMINDERS } from '../../router/constants';
import PermissionsService, {
  PERMISSION_REMINDERS,
  PERMISSION_REMINDERS_OWNED_BY_USER,
} from '../../Services/PermissionsService';
import {
  TABLE_REMINDERS,
  TABLE_REMINDERS_LOGS,
  TABLE_SENT_REMINDERS,
} from '../../Services/TableService';
import { Tab } from '../TabComponent/types';
import { IValidationRule, TValidationRuleParam } from '../../Services/FormikValidatorService';
import { ReadonlyRules, ReminderRequest, ReminderResponse, RemindersFormProps } from './types';
import {
  MAX_REPEAT_INTERVAL_DAYS,
  MAX_REPEAT_INTERVAL_MONTHS,
  MAX_REPEAT_INTERVAL_YEARS,
  REMINDER_STATUS_FAILED,
  REMINDER_STATUS_PENDING,
  REMINDER_STATUS_PENDING_CONFIRM,
  REMINDER_STATUS_PROCESSED,
} from './constants';
import { IVehicles } from '../Vehicles/types';
import { deleteRequest, patchRequest } from '../../Services/HttpService';

export const RemindersTableSettingsKeys = {
  customer: 'table_list.columns.customerId',
  name: 'table_list.columns.reminders.name',
  confirmable: 'table_list.columns.reminders.confirmable',
  notificationSettings: 'table_list.columns.reminders.notificationSettings',
  nextDate: 'table_list.columns.reminders.nextDate',
  earlyNextDate: 'table_list.columns.reminders.earlyNextDate',
  earlyNextMileage: 'table_list.columns.reminders.earlyNextMileage',
  nextMileage: 'table_list.columns.reminders.nextMileage',
  licenceNumber: 'table_list.columns.canRefuelings.licenceNumber',
  vehicleName: 'table_list.columns.refuelings.vehicleName',
  type: 'table_list.columns.reminders.type',
  status: 'table_list.columns.reminders.status',
  createdAt: 'table_list.columns.createdAt',
  createdBy: 'table_list.columns.createdBy',
  modifiedAt: 'table_list.columns.modifiedAt',
  modifiedBy: 'table_list.columns.modifiedBy',
};
export const RemindersLogsSettingsKeys = {
  sentAt: 'table_list.columns.reminders.logs.sentAt',
  customer: 'table_list.columns.customerId',
  reminder: 'table_list.columns.reminders.logs.reminder',
  recipient: 'table_list.columns.reminders.logs.recipient',
  type: 'table_list.columns.reminders.logs.type',
  message: 'table_list.columns.reminders.logs.message',
};

export const tabsByPermission = (
  tabs: string[],
  active: string,
  selectedCustomers: number[]
): Tab[] => {
  const enabledTabs: Tab[] = [];
  tabs.forEach((tabItem: string) => {
    if (
      tabItem === TABLE_REMINDERS &&
      (PermissionsService.hasAccess(PERMISSION_REMINDERS, ['read'], selectedCustomers) ||
        PermissionsService.hasAccess(
          PERMISSION_REMINDERS_OWNED_BY_USER,
          ['read'],
          selectedCustomers
        ))
    ) {
      enabledTabs.push({
        name: tabItem,
        isActive: tabItem === active,
        url: `${URL_REMINDERS}`,
      });
    }
    if (
      tabItem === TABLE_SENT_REMINDERS &&
      (PermissionsService.hasAccess(PERMISSION_REMINDERS, ['read'], selectedCustomers) ||
        PermissionsService.hasAccess(
          PERMISSION_REMINDERS_OWNED_BY_USER,
          ['read'],
          selectedCustomers
        ))
    ) {
      enabledTabs.push({
        name: tabItem,
        isActive: tabItem === active,
        url: `${URL_SENT_REMINDERS}`,
      });
    }
    if (
      tabItem === TABLE_REMINDERS_LOGS &&
      (PermissionsService.hasAccess(PERMISSION_REMINDERS, ['read'], selectedCustomers) ||
        PermissionsService.hasAccess(
          PERMISSION_REMINDERS_OWNED_BY_USER,
          ['read'],
          selectedCustomers
        ))
    ) {
      enabledTabs.push({
        name: tabItem,
        isActive: tabItem === active,
        url: `${URL_REMINDERS_LOGS}`,
      });
    }
  });
  return enabledTabs;
};

export const resolveLocales = (lang?: string): string[] => {
  const nav = typeof navigator !== 'undefined' ? navigator.language : 'en-US';
  if (!lang) return [nav, 'en-US'];
  return [lang, `${lang}-${lang.toUpperCase()}`, nav, 'en-US'];
};

const isAxiosResponse = <T>(x: unknown): x is AxiosResponse<T> =>
  typeof x === 'object' && x !== null && 'data' in (x as Record<string, unknown>);

const allowedParams: readonly TValidationRuleParam[] = [
  'required',
  'maxLength',
  'minDate',
  'min',
  'max',
] as const;

const toValidationParam = (p: unknown): TValidationRuleParam | null => {
  return typeof p === 'string' && (allowedParams as readonly string[]).includes(p)
    ? (p as TValidationRuleParam)
    : null;
};

export const normalizeValidationResponse = (resp: unknown): IValidationApiResponse => {
  const raw = isAxiosResponse<Record<string, unknown>>(resp) ? resp.data : resp;

  const acc: Record<string, IValidationRule[]> = {};
  if (typeof raw !== 'object' || raw === null) return acc;

  Object.entries(raw as Record<string, unknown>).forEach(([key, val]) => {
    if (!Array.isArray(val)) return;

    const rules: IValidationRule[] = val
      .map((item) => {
        if (typeof item !== 'object' || item === null) return undefined;
        const obj = item as { param?: unknown; value?: unknown; message?: unknown };

        const param = toValidationParam(obj.param);
        if (!param) return undefined; // ignores unknowns and 'callback'

        return {
          param,
          value: obj.value as unknown,
          message: typeof obj.message === 'string' ? obj.message : '',
        } as IValidationRule;
      })
      .filter((x): x is IValidationRule => Boolean(x));

    if (rules.length) acc[key] = rules;
  });

  return acc;
};

/**
 * Convert Date object to YYYY-MM-DD string using local timezone
 * This prevents timezone conversion issues when using toISOString()
 * @param date - Date object to convert
 * @returns Date string in YYYY-MM-DD format (e.g., "2025-11-02")
 */
export const formatDateToYYYYMMDD = (date: Date | undefined): string => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const calculateNextDate = (
  scheduleAt: string,
  period: string,
  repeatEveryTime: number
): string => {
  try {
    const date = new Date(scheduleAt);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    switch (period) {
      case 'day':
        date.setDate(date.getDate() + repeatEveryTime);
        break;
      case 'month':
        date.setMonth(date.getMonth() + repeatEveryTime);
        break;
      case 'year':
        date.setFullYear(date.getFullYear() + repeatEveryTime);
        break;
      default:
        return '';
    }

    return formatDateToYYYYMMDD(date);
  } catch (error) {
    console.error('Error calculating next date:', error);
    return '';
  }
};

export const mapAndSetFormValues = (
  response: ReminderResponse,
  isDuplicate?: boolean,
  f?: IntlFormatters['formatMessage']
) => {
  // Helper form change {days, months, years} → "5 day" atď. (kept if needed elsewhere)
  // const mapInterval = (obj?: { days?: number; months?: number; years?: number }) => {
  //   if (!obj) return '';
  //   if (obj.days) return `${obj.days} day`;
  //   if (obj.months) return `${obj.months} month`;
  //   if (obj.years) return `${obj.years} year`;
  //   return '';
  // };

  // Find if repeatInterval (date or mileage)
  const hasDateRepeat =
    !!response.dateInterval &&
    !!response.dateInterval.repeatInterval &&
    (response.dateInterval.repeatInterval.days > 0 ||
      response.dateInterval.repeatInterval.months > 0 ||
      response.dateInterval.repeatInterval.years > 0);

  const hasMileageRepeat =
    !!response.mileageInterval &&
    !!response.mileageInterval.repeatInterval &&
    Number(response.mileageInterval.repeatInterval) > 0;

  // Parsing repeatInterval for recurrenceSettings (only object per types)
  let period = '';
  let repeatEveryTime = 0;
  if (response.dateInterval?.repeatInterval) {
    const ri = response.dateInterval.repeatInterval; // { days, months, years }
    if (ri.months && ri.months > 0) {
      period = 'month';
      repeatEveryTime = ri.months;
    } else if (ri.days && ri.days > 0) {
      period = 'day';
      repeatEveryTime = ri.days;
    } else if (ri.years && ri.years > 0) {
      period = 'year';
      repeatEveryTime = ri.years;
    }
  }

  // Parsing repeatInterval for mileage (number)
  let repeatEveryMileage = 0;
  if (response.mileageInterval?.repeatInterval) {
    repeatEveryMileage = Number(response.mileageInterval.repeatInterval);
  }

  // calculate nextDate
  let nextDate = '';
  if (response.dateInterval?.scheduleAt && period && repeatEveryTime) {
    const baseDate = new Date(response.dateInterval.scheduleAt);
    const next = new Date(baseDate);
    switch (period) {
      case 'month':
        next.setMonth(next.getMonth() + Number(repeatEveryTime));
        break;
      case 'week':
        next.setDate(next.getDate() + Number(repeatEveryTime) * 7);
        break;
      case 'year':
        next.setFullYear(next.getFullYear() + Number(repeatEveryTime));
        break;
      case 'day':
        next.setDate(next.getDate() + Number(repeatEveryTime));
        break;
      default:
        break;
    }
    nextDate = formatDateToYYYYMMDD(next);
  }

  type BackendInterval = { days: number; months: number; years: number };
  const getNotifyBeforeDays = (src?: BackendInterval): number => {
    if (!src) return 0;
    return typeof src.days === 'number' ? src.days : 0;
  };

  // Helper to determine repeatMode for date interval
  const getDateRepeatMode = (): string => {
    if (!hasDateRepeat || !period || !repeatEveryTime) return 'no-repeat';

    // Try to match predefined options
    const value = `${repeatEveryTime}_${period}`;
    const predefinedOptions = ['1_month', '3_month', '6_month', '1_year', '2_year', '4_year'];

    if (predefinedOptions.includes(value)) {
      return value;
    }

    // Otherwise it's custom
    return 'custom';
  };

  // Helper to determine notifyBeforeMode for date interval
  const getDateNotifyBeforeMode = (): string => {
    const notifyDays = getNotifyBeforeDays(response.dateInterval?.notifyBeforeInterval);

    if (!response.dateInterval?.notifyBeforeInterval || notifyDays === null) {
      return 'no-notify';
    }

    // Try to match predefined options
    const predefinedDays = [0, 1, 3, 7, 14, 30];
    if (predefinedDays.includes(notifyDays)) {
      return String(notifyDays);
    }

    // Otherwise it's custom
    return 'custom';
  };

  // Helper to determine repeatMode for mileage interval
  const getMileageRepeatMode = (): string => {
    if (!hasMileageRepeat || !repeatEveryMileage) return 'no-repeat';

    // Try to match predefined km options
    const predefinedKm = [10000, 15000, 20000, 30000, 60000, 120000];

    if (predefinedKm.includes(repeatEveryMileage)) {
      return String(repeatEveryMileage);
    }

    // Otherwise it's custom
    return 'custom';
  };

  const dataForm: RemindersFormProps = {
    type: response.type,
    name: response.name,
    confirmable: response.confirmable ?? false,
    text: response.text,
    customerId: response.customerId,
    recipients: {
      persons: response.recipients.persons?.map((u: number) => String(u)) ?? [],
      allAssignedDrivers: response.recipients.allAssignedDrivers ?? false,
      emails: response.recipients.emails ?? [],
    },
    filters: response.filters
      ? {
          blacklistedUsers: response.filters?.blacklistedUsers?.map((u: number) => String(u)) ?? [],
        }
      : {},
    dateInterval: response.dateInterval
      ? {
          scheduleAt: response.dateInterval.scheduleAt
            ? new Date(response.dateInterval.scheduleAt)
            : new Date(),
          notifyBeforeInterval: getNotifyBeforeDays(response.dateInterval.notifyBeforeInterval),
          repeatInterval: repeatEveryTime,
          repeatMode: getDateRepeatMode(),
          notifyBeforeMode: getDateNotifyBeforeMode(),
        }
      : {
          scheduleAt: new Date(),
          notifyBeforeInterval: undefined,
          repeatInterval: undefined,
          repeatMode: 'no-repeat',
          notifyBeforeMode: 'no-notify',
        },
    mileageInterval: response.mileageInterval
      ? {
          vehicleId: response.mileageInterval.vehicleId ?? undefined,
          scheduleAt: Number(response.mileageInterval.scheduleAt ?? 0),
          notifyBeforeThreshold: Number(response.mileageInterval.notifyBeforeThreshold ?? 0),
          repeatInterval: repeatEveryMileage,
          repeatMode: getMileageRepeatMode(),
        }
      : {
          vehicleId: undefined,
          scheduleAt: 0,
          notifyBeforeThreshold: undefined,
          repeatInterval: undefined,
          repeatMode: 'no-repeat',
        },
    recurrenceSettings: {
      period,
      nextDate,
    },
    notificationSettings: response.notificationSettings || [],
  };
  if (isDuplicate && f) {
    dataForm.name = `${dataForm.name} ${f({ id: 'reminder.duplicate.suffix' })}`;
  }

  return dataForm;
};

export const getAllowedRangeReminders = () => {
  const today = new Date();
  const maxDate = new Date();
  maxDate.setFullYear(today.getFullYear() + 10);
  return [today, maxDate];
};

export const getAllowedRangeRemindersWithPast = () => {
  const today = new Date();
  const minDate = new Date();
  minDate.setFullYear(today.getFullYear() - 2);
  const maxDate = new Date();
  maxDate.setFullYear(today.getFullYear() + 10);
  return [minDate, maxDate];
};

export const getMaxRepeatIntervalByPeriod = (period: string | undefined): number => {
  switch (period) {
    case 'day':
      return MAX_REPEAT_INTERVAL_DAYS;
    case 'month':
      return MAX_REPEAT_INTERVAL_MONTHS;
    case 'year':
      return MAX_REPEAT_INTERVAL_YEARS;
    default:
      return MAX_REPEAT_INTERVAL_DAYS;
  }
};

export const getMaxRepeatIntervalText = (period: string | undefined): string => {
  switch (period) {
    case 'day':
      return `Maximálna hodnota je ${MAX_REPEAT_INTERVAL_DAYS} dní`;
    case 'month':
      return `Maximálna hodnota je ${MAX_REPEAT_INTERVAL_MONTHS} mesiacov (${MAX_REPEAT_INTERVAL_DAYS} dní)`;
    case 'year':
      return `Maximálna hodnota sú ${MAX_REPEAT_INTERVAL_YEARS} roky (${MAX_REPEAT_INTERVAL_DAYS} dní)`;
    default:
      return `Maximálna hodnota je ${MAX_REPEAT_INTERVAL_DAYS} dní`;
  }
};

export const convertRepeatIntervalToDays = (
  repeatInterval: number | undefined,
  period: string | undefined
): number => {
  if (!repeatInterval || !period) return 0;

  switch (period) {
    case 'day':
      return repeatInterval;
    case 'month':
      return Math.floor(repeatInterval * 30.44); // average month
    case 'year':
      return Math.floor(repeatInterval * 365.25); // average year
    default:
      return 0;
  }
};

/**
 * Calculate max value for notifyBeforeInterval based on recurrence settings
 */
export const getMaxNotifyBeforeInterval = (
  formData: RemindersFormProps
): { maxValue: number; maxMessage: string } => {
  let maxValue = MAX_REPEAT_INTERVAL_DAYS;
  let maxMessage = `Maximálna hodnota je ${MAX_REPEAT_INTERVAL_DAYS} dní`;

  const isRecurrenceEnabled =
    formData.dateInterval?.repeatMode && formData.dateInterval.repeatMode !== 'no-repeat';

  if (
    isRecurrenceEnabled &&
    formData.dateInterval?.repeatInterval &&
    formData.recurrenceSettings?.period
  ) {
    const repeatIntervalInDays = convertRepeatIntervalToDays(
      formData.dateInterval.repeatInterval,
      formData.recurrenceSettings.period
    );

    if (repeatIntervalInDays > 0) {
      maxValue = repeatIntervalInDays;
      maxMessage =
        'Interval pre skoršie upozornenie nesmie byť väčší, ako interval opakovania pripomienky.';
    }
  }

  return { maxValue, maxMessage };
};

// Helper functions for resolver
const createRequiredRule = (f: IntlFormatters['formatMessage']) => ({
  param: 'required' as const,
  value: true,
  message: f({ id: 'form.required' }),
});

// universal function to validate a field against backend rules
const validateFieldWithBackendRules = (
  fieldValue: number | null | undefined,
  backendRules: IValidationRule[],
  f: IntlFormatters['formatMessage'],
  customMinLogic?: (
    value: number,
    minRule: IValidationRule
  ) => { hasError: boolean; message: string }
): { hasError: boolean; message: string } => {
  if (fieldValue == null || Number.isNaN(Number(fieldValue))) {
    return { hasError: true, message: f({ id: 'form.required' }) };
  }

  const numValue = Number(fieldValue);

  // Backend min validation
  const minRule = backendRules.find((rule) => rule.param === 'min');
  if (minRule) {
    // If custom logic is defined, use it (for mileageInterval.scheduleAt with odometer)
    if (customMinLogic) {
      const customResult = customMinLogic(numValue, minRule);
      if (customResult.hasError) {
        return customResult;
      }
    } else if (numValue < Number(minRule.value)) {
      // Standard min validation (must be greater than or equal to min value)
      return {
        hasError: true,
        message: minRule.message || f({ id: 'form.rules.costs.valueMustBeGreater' }),
      };
    }
  }

  // Backend max validation
  const maxRule = backendRules.find((rule) => rule.param === 'max');
  if (maxRule && numValue > Number(maxRule.value)) {
    return {
      hasError: true,
      message: maxRule.message || f({ id: 'form.rules.costs.valueMustBeLess' }),
    };
  }

  return { hasError: false, message: '' };
};

export const addBasicValidationRules = (
  rules: IValidationApiResponse,
  validation: IValidationApiResponse | null,
  f: IntlFormatters['formatMessage']
): void => {
  const requiredRule = createRequiredRule(f);

  // Remove validation for boolean fields (active, confirmable)
  // because they always have a value (true/false) and cannot be "empty"
  delete rules.active;
  delete rules.confirmable;

  // These fields should ALWAYS be required, regardless of backend validation
  if (validation === null) {
    // if we don't have backend validation, add all required rules
    rules.type = [...(rules.type ?? []), requiredRule];
    rules.customerId = [...(rules.customerId ?? []), requiredRule];
    // rules.text = [...(rules.text ?? []), requiredRule];
    rules.name = [...(rules.name ?? []), requiredRule];
  } else {
    // If we have backend validation, add only those fields that are not covered by the backend
    if (!rules.type) {
      rules.type = [requiredRule];
    }
    if (!rules.customerId) {
      rules.customerId = [requiredRule];
    }
  }
};

/**
 * Calculate maximum days before notification based on schedule date and current date
 * Returns max days that can be notified before the schedule date
 *
 * IMPORTANT: Notification must be at least 1 day in the future (not today)
 * Example:
 * - Today: 22.10.2025
 * - scheduleAt: 23.10.2025 (1 day difference)
 * - Max notifyBeforeInterval: 0 days (notification would be today, not allowed)
 * - scheduleAt: 24.10.2025 (2 days difference)
 * - Max notifyBeforeInterval: 1 day (notification would be tomorrow)
 */
export const getMaxDaysBeforeNotification = (scheduleAt: Date | string | undefined): number => {
  if (!scheduleAt) return 0;

  try {
    const scheduleDate = scheduleAt instanceof Date ? new Date(scheduleAt) : new Date(scheduleAt);
    const today = new Date();

    // Reset time to compare only dates
    today.setHours(0, 0, 0, 0);
    scheduleDate.setHours(0, 0, 0, 0);

    if (Number.isNaN(scheduleDate.getTime())) {
      return 0;
    }

    // Calculate difference in days
    const diffTime = scheduleDate.getTime() - today.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    // Notification must be at least tomorrow (not today)
    // So if schedule is 2 days away, max notify is 1 day
    // Example: scheduleAt = 30.10, today = 23.10 → diffDays = 7 → maxNotifyDays = 6
    const maxNotifyDays = diffDays - 1;

    return maxNotifyDays > 0 ? maxNotifyDays : 0;
  } catch (error) {
    console.error('Error calculating max days before notification:', error);
    return 0;
  }
};

/**
 * Generate validation message for notifyBeforeInterval with translation
 * @param maxDays - Maximum allowed days
 * @param f - IntlFormatMessage function
 * @returns Formatted validation message
 */
export const getNotifyBeforeIntervalValidationMessage = (
  maxDays: number,
  f: IntlFormatters['formatMessage']
): string => {
  if (maxDays === 0) {
    // Schedule date is too close (today or tomorrow)
    // Notification would be today, which is not allowed
    return f({ id: 'reminder.validation.dateInterval.futureDate' });
  }

  return f({ id: 'reminder.validation.dateInterval.maxDays' }, { count: maxDays });
};

/**
 * Validate notifyBeforeInterval against schedule date
 * Returns error message if validation fails, null otherwise
 */
export const validateNotifyBeforeInterval = (
  scheduleAt: Date | string | undefined,
  notifyBeforeInterval: number | undefined,
  f: IntlFormatters['formatMessage']
): string | null => {
  if (!notifyBeforeInterval || notifyBeforeInterval === 0) {
    return null;
  }

  const maxDays = getMaxDaysBeforeNotification(scheduleAt);

  if (notifyBeforeInterval > maxDays) {
    return getNotifyBeforeIntervalValidationMessage(maxDays, f);
  }

  return null;
};

/**
 * Validate notifyBeforeThreshold against current odometer
 * Returns error message if validation fails, null otherwise
 *
 * IMPORTANT: Notification must be at least 1 km higher than current odometer
 * Example:
 * - Current odometer: 45000 km
 * - scheduleAt: 46000 km (1000 km difference)
 * - Max notifyBeforeThreshold: 999 km (notification at 45001 km, not 45000 km)
 * - scheduleAt: 47000 km (2000 km difference)
 * - Max notifyBeforeThreshold: 1999 km (notification at 45001 km)
 */
export const validateNotifyBeforeThreshold = (
  scheduleAt: number | undefined,
  notifyBeforeThreshold: number | undefined,
  currentOdometer: number | undefined,
  f: IntlFormatters['formatMessage']
): string | null => {
  if (!notifyBeforeThreshold || notifyBeforeThreshold === 0) {
    return null;
  }

  if (!scheduleAt) {
    return null;
  }

  // Use current odometer value (already numeric)
  const odometerValue = currentOdometer || 0;

  // Calculate notification mileage (when notification will be triggered)
  const notificationMileage = scheduleAt - notifyBeforeThreshold;

  // Notification mileage must be HIGHER than current odometer (not equal)
  // This ensures notification happens in the future, not at current mileage
  if (notificationMileage <= odometerValue) {
    return f({ id: 'reminder.validation.mileageInterval.notifyBeforeThreshold.inPast' });
  }

  return null;
};

/**
 * Validate that repeatInterval is greater than notifyBeforeInterval
 * This ensures notifications don't overlap or come before previous reminder
 * Returns error message if validation fails, null otherwise
 */
export const validateNotifyBeforeIntervalAgainstRepeat = (
  notifyBeforeInterval: number | undefined,
  repeatInterval: number | undefined,
  period: string | undefined,
  f: IntlFormatters['formatMessage']
): string | null => {
  if (!notifyBeforeInterval || notifyBeforeInterval === 0) {
    return null;
  }

  if (!repeatInterval || repeatInterval === 0 || !period) {
    return null;
  }

  // Convert repeatInterval to days for comparison
  const repeatIntervalInDays = convertRepeatIntervalToDays(repeatInterval, period);

  // Ensure both values are numbers for comparison
  const notifyBeforeNum = Number(notifyBeforeInterval);
  const repeatInDaysNum = Number(repeatIntervalInDays);

  // repeatInterval must be GREATER than notifyBeforeInterval (not equal)
  if (repeatInDaysNum < notifyBeforeNum) {
    return f({ id: 'reminder.validation.dateInterval.notifyBeforeTooLarge' });
  }

  return null;
};

/**
 * Validate that repeatInterval is greater than notifyBeforeThreshold (mileage)
 * This ensures notifications don't overlap or come before previous reminder
 * Returns error message if validation fails, null otherwise
 */
export const validateNotifyBeforeThresholdAgainstRepeat = (
  notifyBeforeThreshold: number | undefined,
  repeatInterval: number | undefined,
  f: IntlFormatters['formatMessage']
): string | null => {
  if (!notifyBeforeThreshold || notifyBeforeThreshold === 0) {
    return null;
  }

  if (!repeatInterval || repeatInterval === 0) {
    return null;
  }

  // Ensure both values are numbers for comparison
  const notifyBeforeNum = Number(notifyBeforeThreshold);
  const repeatIntervalNum = Number(repeatInterval);

  // repeatInterval must be GREATER than notifyBeforeThreshold (not equal)
  if (repeatIntervalNum < notifyBeforeNum) {
    return f({ id: 'reminder.validation.mileageInterval.notifyBeforeTooLarge' });
  }

  return null;
};

/**
 * Validate that repeatInterval is greater than notifyBeforeInterval (for repeatInterval field)
 * Returns error message if validation fails, null otherwise
 */
export const validateRepeatIntervalAgainstNotifyBefore = (
  notifyBeforeInterval: number | undefined,
  repeatInterval: number | undefined,
  period: string | undefined,
  f: IntlFormatters['formatMessage']
): string | null => {
  if (!notifyBeforeInterval || notifyBeforeInterval === 0) {
    return null;
  }

  if (!repeatInterval || repeatInterval === 0 || !period) {
    return null;
  }

  // Convert repeatInterval to days for comparison
  const repeatIntervalInDays = convertRepeatIntervalToDays(repeatInterval, period);

  // Ensure both values are numbers for comparison
  const notifyBeforeNum = Number(notifyBeforeInterval);
  const repeatInDaysNum = Number(repeatIntervalInDays);

  // repeatInterval must be GREATER than notifyBeforeInterval (not equal)
  if (repeatInDaysNum < notifyBeforeNum) {
    return f({ id: 'reminder.validation.dateInterval.repeatIntervalTooSmall' });
  }

  return null;
};

/**
 * Validate that repeatInterval is greater than notifyBeforeThreshold (mileage, for repeatInterval field)
 * Returns error message if validation fails, null otherwise
 */
export const validateRepeatIntervalAgainstNotifyBeforeThreshold = (
  notifyBeforeThreshold: number | undefined,
  repeatInterval: number | undefined,
  f: IntlFormatters['formatMessage']
): string | null => {
  if (!notifyBeforeThreshold || notifyBeforeThreshold === 0) {
    return null;
  }

  if (!repeatInterval || repeatInterval === 0) {
    return null;
  }

  // Ensure both values are numbers for comparison
  const notifyBeforeNum = Number(notifyBeforeThreshold);
  const repeatIntervalNum = Number(repeatInterval);

  // repeatInterval must be GREATER than notifyBeforeThreshold (not equal)
  if (repeatIntervalNum < notifyBeforeNum) {
    return f({ id: 'reminder.validation.mileageInterval.repeatIntervalTooSmall' });
  }

  return null;
};

export const addDynamicValidationRules = (
  rules: IValidationApiResponse,
  formData: RemindersFormProps,
  f: IntlFormatters['formatMessage']
): void => {
  const requiredRule = createRequiredRule(f);

  const recipientsRule = {
    param: 'required' as const,
    value: true,
    message: f({ id: 'form.required' }),
  };

  // Recipients validation
  if (formData.customerId) {
    const users = formData.recipients?.persons ?? [];
    const allAssignedDrivers = formData.recipients?.allAssignedDrivers;

    // do not validate if "allAssignedDrivers" is selected or at least one user is selected
    if (!allAssignedDrivers && users.length === 0) {
      rules['recipients.persons'] = [...(rules['recipients.persons'] ?? []), recipientsRule];
    }
  }

  // Schedule validation
  if (formData.type === 'date' || formData.type === 'dateAndMileage') {
    rules['dateInterval.scheduleAt'] = [...(rules['dateInterval.scheduleAt'] ?? []), requiredRule];
  }
  if (formData.type === 'mileage' || formData.type === 'dateAndMileage') {
    rules['mileageInterval.scheduleAt'] = [
      ...(rules['mileageInterval.scheduleAt'] ?? []),
      requiredRule,
    ];
    rules['mileageInterval.vehicleId'] = [
      ...(rules['mileageInterval.vehicleId'] ?? []),
      requiredRule,
    ];

    const hasBackendMinMax = rules['mileageInterval.scheduleAt']?.some(
      (rule) => rule.param === 'min' || rule.param === 'max'
    );

    if (!hasBackendMinMax) {
      // Backend not having min/max validation, add custom validation
      // Note: Actual odometer check is done in addHardEnforceErrors
      // where we have access to vehicleIdOptions
      const customMinRule = {
        param: 'min' as const,
        value: 0,
        message: f({ id: 'form.rules.costs.valueMustBeGreater' }),
      };

      rules['mileageInterval.scheduleAt'] = [...rules['mileageInterval.scheduleAt'], customMinRule];
    }
  }

  // Notify before validation - only when custom mode is selected
  if (formData.dateInterval?.notifyBeforeMode === 'custom') {
    rules['dateInterval.notifyBeforeInterval'] = [
      ...(rules['dateInterval.notifyBeforeInterval'] ?? []),
      requiredRule,
    ];

    // Add basic min/max validation if not provided by backend
    const hasBackendMinMax = rules['dateInterval.notifyBeforeInterval']?.some(
      (rule) => rule.param === 'min' || rule.param === 'max'
    );

    if (!hasBackendMinMax) {
      // Add basic min validation (must be greater than or equal to 1)
      const customMinRule = {
        param: 'min' as const,
        value: 1,
        message: f({ id: 'form.rules.costs.valueMustBeGreater' }),
      };
      const { maxValue, maxMessage } = getMaxNotifyBeforeInterval(formData);

      const customMaxRule = {
        param: 'max' as const,
        value: maxValue,
        message: maxMessage,
      };

      rules['dateInterval.notifyBeforeInterval'] = [
        ...rules['dateInterval.notifyBeforeInterval'],
        customMinRule,
        customMaxRule,
      ];
    }
  }

  // Mileage notifyBeforeThreshold validation - always validate if value is provided
  if (formData.mileageInterval?.notifyBeforeThreshold !== undefined) {
    rules['mileageInterval.notifyBeforeThreshold'] = [
      ...(rules['mileageInterval.notifyBeforeThreshold'] ?? []),
    ];

    // Add basic min/max validation if not provided by backend
    const hasBackendMinMax = rules['mileageInterval.notifyBeforeThreshold']?.some(
      (rule) => rule.param === 'min' || rule.param === 'max'
    );

    if (!hasBackendMinMax) {
      // Add basic min validation (must be greater than or equal to 0)
      const customMinRule = {
        param: 'min' as const,
        value: 0,
        message: f({ id: 'reminder.validation.mileageInterval.notifyBeforeThreshold.minValue' }),
      };

      rules['mileageInterval.notifyBeforeThreshold'] = [
        ...rules['mileageInterval.notifyBeforeThreshold'],
        customMinRule,
      ];
    }
  }

  // Recurrence validation - check if repeatMode is set and not 'no-repeat' for either date or mileage
  const isDateRecurrenceEnabled =
    formData.dateInterval?.repeatMode && formData.dateInterval.repeatMode !== 'no-repeat';
  const isMileageRecurrenceEnabled =
    formData.mileageInterval?.repeatMode && formData.mileageInterval.repeatMode !== 'no-repeat';

  // Date recurrence validation
  if (isDateRecurrenceEnabled && (formData.type === 'date' || formData.type === 'dateAndMileage')) {
    rules['recurrenceSettings.period'] = [
      ...(rules['recurrenceSettings.period'] ?? []),
      requiredRule,
    ];
    rules['dateInterval.repeatInterval'] = [
      ...(rules['dateInterval.repeatInterval'] ?? []),
      requiredRule,
    ];
    const hasBackendMax = rules['dateInterval.repeatInterval']?.some(
      (rule) => rule.param === 'max'
    );

    if (!hasBackendMax) {
      const period = formData.recurrenceSettings?.period;
      const maxValue = getMaxRepeatIntervalByPeriod(period);
      const maxMessage = getMaxRepeatIntervalText(period);

      const maxRule = {
        param: 'max' as const,
        value: maxValue,
        message: maxMessage,
      };

      rules['dateInterval.repeatInterval'] = [...rules['dateInterval.repeatInterval'], maxRule];
    }
  }

  // Mileage recurrence validation
  if (
    isMileageRecurrenceEnabled &&
    (formData.type === 'mileage' || formData.type === 'dateAndMileage')
  ) {
    rules['mileageInterval.repeatInterval'] = [
      ...(rules['mileageInterval.repeatInterval'] ?? []),
      requiredRule,
    ];
    // firstOdometerNotification je disabled
  }
};

/**
 * Validates mileageInterval.scheduleAt field with custom business logic
 * Validates against backend rules and current odometer reading
 */
const validateMileageScheduleAt = (
  errors: import('react-hook-form').FieldErrors<RemindersFormProps>,
  formData: RemindersFormProps,
  f: IntlFormatters['formatMessage'],
  rules?: IValidationApiResponse,
  currentOdometer?: number
): import('react-hook-form').FieldErrors<RemindersFormProps> => {
  const v = formData.mileageInterval?.scheduleAt;
  const missing = v == null || Number.isNaN(Number(v));

  if (missing) {
    const msg = f({ id: 'form.required' });
    return {
      ...errors,
      mileageInterval: {
        ...(errors.mileageInterval ?? {}),
        scheduleAt: { type: 'required', message: msg },
      },
    };
  }

  const scheduleAtValue = Number(v);
  const backendRules = rules?.['mileageInterval.scheduleAt'] || [];

  const minRule = backendRules.find((rule) => rule.param === 'min');
  const maxRule = backendRules.find((rule) => rule.param === 'max');

  let hasError = false;
  let errorMsg = '';

  // Use provided current odometer value
  const odometerValue = currentOdometer ?? 0;

  // Backend min validation
  if (minRule) {
    const backendMinValue = Number(minRule.value);
    const effectiveMinValue = Math.max(backendMinValue, odometerValue);

    if (scheduleAtValue < effectiveMinValue) {
      hasError = true;

      // If odometer is greater than backend min, use odometer message
      if (odometerValue > backendMinValue && odometerValue > 0) {
        errorMsg = f({ id: 'reminder.validation.mileageInterval.scheduleAt.higherThanOdometer' });
      } else {
        errorMsg = minRule.message || f({ id: 'form.rules.costs.valueMustBeGreater' });
      }
    }
  }

  // Backend max validation
  if (!hasError && maxRule && scheduleAtValue > Number(maxRule.value)) {
    hasError = true;
    errorMsg = maxRule.message || f({ id: 'form.rules.costs.valueMustBeLess' });
  }

  // Custom min validation when no backend min/max
  if (!hasError && !minRule && !maxRule) {
    const minValue = !Number.isNaN(odometerValue) && odometerValue > 0 ? odometerValue : 0;

    if (scheduleAtValue < minValue) {
      hasError = true;
      errorMsg =
        odometerValue > 0
          ? f({ id: 'reminder.validation.mileageInterval.scheduleAt.higherThanOdometer' })
          : f({ id: 'form.rules.costs.valueMustBeGreater' });
    }
  }

  // Add or remove error
  if (hasError) {
    return {
      ...errors,
      mileageInterval: {
        ...(errors.mileageInterval ?? {}),
        scheduleAt: { type: 'min', message: errorMsg },
      },
    };
  }

  if (errors.mileageInterval?.scheduleAt) {
    const { scheduleAt: _scheduleAt, ...restMileageInterval } = errors.mileageInterval;
    if (Object.keys(restMileageInterval).length === 0) {
      const { mileageInterval: _mileageInterval, ...restErrors } = errors;
      return restErrors;
    }

    return {
      ...errors,
      mileageInterval: restMileageInterval,
    };
  }

  return errors;
};

/**
 * Validates dateInterval.notifyBeforeInterval field with custom business logic
 * Uses frontend validation to ensure notification date is in the future
 * and notifyBeforeInterval is not greater than repeatInterval
 */
const validateDateNotifyBeforeInterval = (
  errors: import('react-hook-form').FieldErrors<RemindersFormProps>,
  formData: RemindersFormProps,
  f: IntlFormatters['formatMessage'],
  rules?: IValidationApiResponse
): import('react-hook-form').FieldErrors<RemindersFormProps> => {
  // Only validate when notifyBeforeMode is not 'no-notify'
  if (
    formData.dateInterval?.notifyBeforeMode &&
    formData.dateInterval.notifyBeforeMode !== 'no-notify'
  ) {
    const v = formData.dateInterval?.notifyBeforeInterval;
    const scheduleAt = formData.dateInterval?.scheduleAt;

    // Check if custom mode is selected and value is missing
    if (formData.dateInterval.notifyBeforeMode === 'custom') {
      const isEmpty =
        v === undefined ||
        v === null ||
        (typeof v === 'number' && Number.isNaN(v)) ||
        String(v).trim() === '';

      if (isEmpty) {
        return {
          ...errors,
          dateInterval: {
            ...(errors.dateInterval ?? {}),
            notifyBeforeInterval: {
              type: 'required',
              message: f({ id: 'form.required' }),
            },
          },
        };
      }
    }

    // Check if value is between 1 and 100
    if (v !== undefined && v !== null) {
      const numValue = Number(v);
      if (numValue < 1 || numValue > 100) {
        return {
          ...errors,
          dateInterval: {
            ...(errors.dateInterval ?? {}),
            notifyBeforeInterval: {
              type: 'custom',
              message: f({ id: 'reminder.validation.dateInterval.notifyBeforeInterval.range' }),
            },
          },
        };
      }
    }

    // First check: notification date must be in future
    let frontendError = validateNotifyBeforeInterval(scheduleAt, v, f);

    // Second check: if recurrence enabled, notifyBefore must not exceed repeatInterval
    const isRecurrenceEnabled =
      formData.dateInterval?.repeatMode && formData.dateInterval.repeatMode !== 'no-repeat';

    if (!frontendError && isRecurrenceEnabled) {
      const repeatInterval = formData.dateInterval?.repeatInterval;
      const period = formData.recurrenceSettings?.period;

      frontendError = validateNotifyBeforeIntervalAgainstRepeat(v, repeatInterval, period, f);
    }

    if (frontendError) {
      return {
        ...errors,
        dateInterval: {
          ...(errors.dateInterval ?? {}),
          notifyBeforeInterval: { type: 'custom', message: frontendError },
        },
      };
    }

    // Then check backend rules
    const backendRules = rules?.['dateInterval.notifyBeforeInterval'] || [];
    const validationResult = validateFieldWithBackendRules(v, backendRules, f);

    if (validationResult.hasError) {
      return {
        ...errors,
        dateInterval: {
          ...(errors.dateInterval ?? {}),
          notifyBeforeInterval: { type: 'min', message: validationResult.message },
        },
      };
    }

    if (errors.dateInterval?.notifyBeforeInterval) {
      const { notifyBeforeInterval: _notifyBeforeInterval, ...restDateInterval } =
        errors.dateInterval;
      if (Object.keys(restDateInterval).length === 0) {
        const { dateInterval: _dateInterval, ...restErrors } = errors;
        return restErrors;
      }

      return {
        ...errors,
        dateInterval: restDateInterval,
      };
    }
  } else if (errors.dateInterval?.notifyBeforeInterval) {
    const { notifyBeforeInterval: _notifyBeforeInterval, ...restDateInterval } =
      errors.dateInterval;
    if (Object.keys(restDateInterval).length === 0) {
      const { dateInterval: _dateInterval, ...restErrors } = errors;
      return restErrors;
    }

    return {
      ...errors,
      dateInterval: restDateInterval,
    };
  }

  return errors;
};

/**
 * Validates mileageInterval.notifyBeforeThreshold field with custom business logic
 * Uses frontend validation to ensure notification mileage is higher than current odometer
 * and notifyBeforeThreshold is not greater than repeatInterval
 */
const validateMileageNotifyBeforeThreshold = (
  errors: import('react-hook-form').FieldErrors<RemindersFormProps>,
  formData: RemindersFormProps,
  f: IntlFormatters['formatMessage'],
  rules?: IValidationApiResponse,
  currentOdometer?: number
): import('react-hook-form').FieldErrors<RemindersFormProps> => {
  // Only validate when notifyBeforeThreshold has a value
  if (
    formData.mileageInterval?.notifyBeforeThreshold !== undefined &&
    formData.mileageInterval.notifyBeforeThreshold !== null
  ) {
    const v = formData.mileageInterval.notifyBeforeThreshold;
    const scheduleAt = formData.mileageInterval.scheduleAt;
    // Use provided current odometer value
    const currentOdometerValue = currentOdometer ?? 0;

    // First check: notification mileage must be higher than current odometer (only for positive values)
    let frontendError =
      v > 0 ? validateNotifyBeforeThreshold(scheduleAt, v, currentOdometerValue, f) : null;

    // Second check: if recurrence enabled, notifyBefore must not exceed repeatInterval
    const isRecurrenceEnabled =
      formData.mileageInterval?.repeatMode && formData.mileageInterval.repeatMode !== 'no-repeat';

    if (!frontendError && isRecurrenceEnabled) {
      const repeatInterval = formData.mileageInterval.repeatInterval;

      frontendError = validateNotifyBeforeThresholdAgainstRepeat(v, repeatInterval, f);
    }

    if (frontendError) {
      return {
        ...errors,
        mileageInterval: {
          ...(errors.mileageInterval ?? {}),
          notifyBeforeThreshold: { type: 'custom', message: frontendError },
        },
      };
    }

    // Then check backend rules
    const backendRules = rules?.['mileageInterval.notifyBeforeThreshold'] || [];
    const validationResult = validateFieldWithBackendRules(v, backendRules, f);

    if (validationResult.hasError) {
      return {
        ...errors,
        mileageInterval: {
          ...(errors.mileageInterval ?? {}),
          notifyBeforeThreshold: { type: 'min', message: validationResult.message },
        },
      };
    }

    if (errors.mileageInterval?.notifyBeforeThreshold) {
      const { notifyBeforeThreshold: _notifyBeforeThreshold, ...restMileageInterval } =
        errors.mileageInterval;
      if (Object.keys(restMileageInterval).length === 0) {
        const { mileageInterval: _mileageInterval, ...restErrors } = errors;
        return restErrors;
      }

      return {
        ...errors,
        mileageInterval: restMileageInterval,
      };
    }
  } else if (errors.mileageInterval?.notifyBeforeThreshold) {
    const { notifyBeforeThreshold: _notifyBeforeThreshold, ...restMileageInterval } =
      errors.mileageInterval;
    if (Object.keys(restMileageInterval).length === 0) {
      const { mileageInterval: _mileageInterval, ...restErrors } = errors;
      return restErrors;
    }

    return {
      ...errors,
      mileageInterval: restMileageInterval,
    };
  }

  return errors;
};

/**
 * Validates dateInterval.repeatInterval field with custom business logic
 * Also validates relationship with notifyBeforeInterval
 */
const validateDateRepeatInterval = (
  errors: import('react-hook-form').FieldErrors<RemindersFormProps>,
  formData: RemindersFormProps,
  f: IntlFormatters['formatMessage'],
  rules?: IValidationApiResponse
): import('react-hook-form').FieldErrors<RemindersFormProps> => {
  // Check if repeatMode is set and not 'no-repeat'
  const isRecurrenceEnabled =
    formData.dateInterval?.repeatMode && formData.dateInterval.repeatMode !== 'no-repeat';

  if (!isRecurrenceEnabled) {
    return errors;
  }

  const repeatInterval = formData.dateInterval?.repeatInterval;
  const backendRules = rules?.['dateInterval.repeatInterval'] || [];

  // Check if custom mode is selected and value is missing
  if (formData.dateInterval?.repeatMode === 'custom') {
    const isEmpty =
      repeatInterval === undefined ||
      repeatInterval === null ||
      (typeof repeatInterval === 'number' && Number.isNaN(repeatInterval)) ||
      String(repeatInterval).trim() === '';

    if (isEmpty) {
      return {
        ...errors,
        dateInterval: {
          ...(errors.dateInterval ?? {}),
          repeatInterval: {
            type: 'required',
            message: f({ id: 'form.required' }),
          },
        },
      };
    }
  }

  // Add max validation if not provided by backend
  const hasBackendMax = backendRules.some((rule) => rule.param === 'max');
  if (!hasBackendMax) {
    const period = formData.recurrenceSettings?.period;
    const maxValue = getMaxRepeatIntervalByPeriod(period);
    const maxMessage = getMaxRepeatIntervalText(period);
    backendRules.push({
      param: 'max',
      value: maxValue,
      message: maxMessage,
    });
  }

  // Backend validation
  const validationResult = validateFieldWithBackendRules(repeatInterval, backendRules, f);

  if (validationResult.hasError) {
    return {
      ...errors,
      dateInterval: {
        ...(errors.dateInterval ?? {}),
        repeatInterval: { type: 'max', message: validationResult.message },
      },
    };
  }

  // Custom validation: repeatInterval must be greater than notifyBeforeInterval
  const notifyBeforeInterval = formData.dateInterval?.notifyBeforeInterval;
  const period = formData.recurrenceSettings?.period;

  const frontendError = validateRepeatIntervalAgainstNotifyBefore(
    notifyBeforeInterval,
    repeatInterval,
    period,
    f
  );

  if (frontendError) {
    return {
      ...errors,
      dateInterval: {
        ...(errors.dateInterval ?? {}),
        repeatInterval: { type: 'custom', message: frontendError },
      },
    };
  }

  // Clear error if validation passed
  if (errors.dateInterval?.repeatInterval) {
    const { repeatInterval: _repeatInterval, ...restDateInterval } = errors.dateInterval;
    if (Object.keys(restDateInterval).length === 0) {
      const { dateInterval: _dateInterval, ...restErrors } = errors;
      return restErrors;
    }

    return {
      ...errors,
      dateInterval: restDateInterval,
    };
  }

  return errors;
};

/**
 * Validates mileageInterval.repeatInterval field with custom business logic
 * Also validates relationship with notifyBeforeThreshold
 */
const validateMileageRepeatInterval = (
  errors: import('react-hook-form').FieldErrors<RemindersFormProps>,
  formData: RemindersFormProps,
  f: IntlFormatters['formatMessage'],
  rules?: IValidationApiResponse
): import('react-hook-form').FieldErrors<RemindersFormProps> => {
  const isRecurrenceEnabled =
    formData.mileageInterval?.repeatMode && formData.mileageInterval.repeatMode !== 'no-repeat';

  if (!isRecurrenceEnabled) {
    return errors;
  }

  const repeatInterval = formData.mileageInterval?.repeatInterval;
  const backendRules = rules?.['mileageInterval.repeatInterval'] || [];

  // Check if custom mode is selected and value is missing or empty
  if (formData.mileageInterval?.repeatMode === 'custom') {
    const missing = repeatInterval == null || Number.isNaN(Number(repeatInterval));

    if (missing) {
      return {
        ...errors,
        mileageInterval: {
          ...(errors.mileageInterval ?? {}),
          repeatInterval: {
            type: 'required',
            message: f({ id: 'form.required' }),
          },
        },
      };
    }
  }

  // Custom range validation for mileage repeat interval (100 - 1,000,000 km)
  // This must be checked BEFORE backend validation
  if (repeatInterval != null && !Number.isNaN(Number(repeatInterval))) {
    const numValue = Number(repeatInterval);
    if (numValue < 100 || numValue > 1000000) {
      return {
        ...errors,
        mileageInterval: {
          ...(errors.mileageInterval ?? {}),
          repeatInterval: {
            type: 'range',
            message: f({ id: 'reminder.validation.mileageInterval.repeatInterval.range' }),
          },
        },
      };
    }
  }

  // Custom validation: repeatInterval must be greater than notifyBeforeThreshold
  const notifyBeforeThreshold = formData.mileageInterval?.notifyBeforeThreshold;

  const frontendError = validateRepeatIntervalAgainstNotifyBeforeThreshold(
    notifyBeforeThreshold,
    repeatInterval,
    f
  );

  if (frontendError) {
    return {
      ...errors,
      mileageInterval: {
        ...(errors.mileageInterval ?? {}),
        repeatInterval: { type: 'custom', message: frontendError },
      },
    };
  }

  // Backend validation (only if frontend validations passed)
  const validationResult = validateFieldWithBackendRules(repeatInterval, backendRules, f);

  if (validationResult.hasError) {
    return {
      ...errors,
      mileageInterval: {
        ...(errors.mileageInterval ?? {}),
        repeatInterval: { type: 'min', message: validationResult.message },
      },
    };
  }

  // Clear error if all validations passed
  if (errors.mileageInterval?.repeatInterval) {
    const { repeatInterval: _repeatInterval, ...restMileageInterval } = errors.mileageInterval;
    if (Object.keys(restMileageInterval).length === 0) {
      const { mileageInterval: _mileageInterval, ...restErrors } = errors;
      return restErrors;
    }

    return {
      ...errors,
      mileageInterval: restMileageInterval,
    };
  }

  return errors;
};

/**
 * Add custom validation for dateAndMileage type - both repeatIntervals must be set or both empty
 */
export const addDateAndMileageRepeatIntervalValidation = (
  formErrors: import('react-hook-form').FieldErrors<RemindersFormProps>,
  formData: RemindersFormProps,
  f: IntlFormatters['formatMessage']
): import('react-hook-form').FieldErrors<RemindersFormProps> => {
  let errors = { ...formErrors };

  if (formData.type === 'dateAndMileage') {
    const hasDateRepeat = !!formData.dateInterval?.repeatInterval;
    const hasMileageRepeat = !!formData.mileageInterval?.repeatInterval;

    // If one is set but not the other, add errors to both fields
    if (hasDateRepeat !== hasMileageRepeat) {
      if (!hasDateRepeat) {
        errors = {
          ...errors,
          dateInterval: {
            ...errors.dateInterval,
            repeatInterval: {
              type: 'custom',
              message: f({
                id: 'reminder.validation.dateAndMileage.bothRepeatIntervalsRequired',
              }),
            },
          },
        };
      }
      if (!hasMileageRepeat) {
        errors = {
          ...errors,
          mileageInterval: {
            ...errors.mileageInterval,
            repeatInterval: {
              type: 'custom',
              message: f({
                id: 'reminder.validation.dateAndMileage.bothRepeatIntervalsRequired',
              }),
            },
          },
        };
      }
    }
  }

  return errors;
};

export const addHardEnforceErrors = (
  formErrors: import('react-hook-form').FieldErrors<RemindersFormProps>,
  formData: RemindersFormProps,
  f: IntlFormatters['formatMessage'],
  rules?: IValidationApiResponse,
  currentOdometer?: number
): import('react-hook-form').FieldErrors<RemindersFormProps> => {
  let errors = { ...formErrors };

  // Recipients hard enforce
  if (formData.customerId) {
    const users = formData.recipients?.persons ?? [];
    const allAssignedDrivers = formData.recipients?.allAssignedDrivers;

    // dont validate if "allAssignedDrivers" is selected or at least one user is selected
    if (!allAssignedDrivers && users.length === 0) {
      const msg = f({ id: 'form.required' });
      const currentRecipients = (errors.recipients ?? {}) as NonNullable<
        import('react-hook-form').FieldErrors<RemindersFormProps>['recipients']
      >;
      errors = {
        ...errors,
        recipients: {
          ...currentRecipients,
          persons: { type: 'required', message: msg },
        },
      };
    } else if (errors.recipients?.persons) {
      const {
        persons: _persons,

        ...restRecipients
      } = errors.recipients || {};

      if (Object.keys(restRecipients).length === 0) {
        const { recipients: _recipients, ...restErrors } = errors;
        errors = restErrors;
      } else {
        errors = {
          ...errors,
          recipients: restRecipients,
        };
      }
    }
  }

  // Date/Mileage hard enforce
  const needDate = ['date', 'dateAndMileage'].includes(String(formData.type));
  const needMileage = ['mileage', 'dateAndMileage'].includes(String(formData.type));

  if (needDate) {
    const v = formData.dateInterval?.scheduleAt;
    const missing = !v || String(v).trim().length === 0;
    if (missing) {
      const msg = f({ id: 'form.required' });
      errors = {
        ...errors,
        dateInterval: {
          ...(errors.dateInterval ?? {}),
          scheduleAt: { type: 'required', message: msg },
        },
      };
    } else {
      // Validate that date is not in the past
      const scheduleDate = new Date(v);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      scheduleDate.setHours(0, 0, 0, 0);

      if (scheduleDate < today) {
        const msg = f({ id: 'reminder.validation.dateInterval.futureDate' });
        errors = {
          ...errors,
          dateInterval: {
            ...(errors.dateInterval ?? {}),
            scheduleAt: { type: 'custom', message: msg },
          },
        };
      } else if (errors.dateInterval?.scheduleAt) {
        const { scheduleAt: _scheduleAt, ...restDateInterval } = errors.dateInterval;
        if (Object.keys(restDateInterval).length === 0) {
          const { dateInterval: _dateInterval, ...restErrors } = errors;
          errors = restErrors;
        } else {
          errors = {
            ...errors,
            dateInterval: restDateInterval,
          };
        }
      }
    }
  }

  if (needMileage) {
    const msg = f({ id: 'form.required' });

    // scheduleAt validation
    errors = validateMileageScheduleAt(errors, formData, f, rules, currentOdometer);

    // vehicleId validation
    const vid = formData.mileageInterval?.vehicleId;
    const missingVehicleId = vid == null || Number.isNaN(Number(vid)) || Number(vid) <= 0;
    if (missingVehicleId) {
      errors = {
        ...errors,
        mileageInterval: {
          ...(errors.mileageInterval ?? {}),
          vehicleId: { type: 'required', message: msg },
        },
      };
    } else if (errors.mileageInterval?.vehicleId) {
      const { vehicleId: _vehicleId, ...restMileageInterval } = errors.mileageInterval;
      if (Object.keys(restMileageInterval).length === 0) {
        const { mileageInterval: _mileageInterval, ...restErrors } = errors;
        errors = restErrors;
      } else {
        errors = {
          ...errors,
          mileageInterval: restMileageInterval,
        };
      }
    }
  }

  // Notify before hard enforce - Date interval
  errors = validateDateNotifyBeforeInterval(errors, formData, f, rules);

  // Notify before hard enforce - Mileage interval
  errors = validateMileageNotifyBeforeThreshold(errors, formData, f, rules, currentOdometer);

  // Recurrence settings hard enforce
  const isDateRecurrenceEnabled =
    formData.dateInterval?.repeatMode && formData.dateInterval.repeatMode !== 'no-repeat';
  const isMileageRecurrenceEnabled =
    formData.mileageInterval?.repeatMode && formData.mileageInterval.repeatMode !== 'no-repeat';

  if (isDateRecurrenceEnabled) {
    const msg = f({ id: 'form.required' });

    // Date recurrence hard enforce
    if (formData.type === 'date' || formData.type === 'dateAndMileage') {
      // period validation
      const period = formData.recurrenceSettings?.period;
      if (!period || String(period).trim().length === 0) {
        errors = {
          ...errors,
          recurrenceSettings: {
            ...(errors.recurrenceSettings ?? {}),
            period: { type: 'required', message: msg },
          },
        };
      } else if (errors.recurrenceSettings?.period) {
        const { period: _period, ...restRecurrenceSettings } = errors.recurrenceSettings;
        if (Object.keys(restRecurrenceSettings).length === 0) {
          const { recurrenceSettings: _recurrenceSettings, ...restErrors } = errors;
          errors = restErrors;
        } else {
          errors = {
            ...errors,
            recurrenceSettings: restRecurrenceSettings,
          };
        }
      }

      // dateInterval.repeatInterval validation (with relationship check)
      errors = validateDateRepeatInterval(errors, formData, f, rules);
    }
  }

  // Mileage recurrence hard enforce
  if (
    isMileageRecurrenceEnabled &&
    (formData.type === 'mileage' || formData.type === 'dateAndMileage')
  ) {
    // mileageInterval.repeatInterval validation (with relationship check)
    errors = validateMileageRepeatInterval(errors, formData, f, rules);
  }

  if (!isDateRecurrenceEnabled && !isMileageRecurrenceEnabled) {
    // remove recurrenceSettings, dateInterval.repeatInterval, mileageInterval.repeatInterval errors if exists
    if (errors.recurrenceSettings) {
      const { recurrenceSettings: _recurrenceSettings, ...restErrors } = errors;
      errors = restErrors;
    }
    if (errors.dateInterval?.repeatInterval) {
      const { repeatInterval: _repeatInterval, ...restDateInterval } = errors.dateInterval;
      if (Object.keys(restDateInterval).length === 0) {
        const { dateInterval: _dateInterval, ...restErrors } = errors;
        errors = restErrors;
      } else {
        errors = {
          ...errors,
          dateInterval: restDateInterval,
        };
      }
    }
    if (errors.mileageInterval?.repeatInterval) {
      const { repeatInterval: _repeatInterval, ...restMileageInterval } = errors.mileageInterval;
      if (Object.keys(restMileageInterval).length === 0) {
        const { mileageInterval: _mileageInterval, ...restErrors } = errors;
        errors = restErrors;
      } else {
        errors = {
          ...errors,
          mileageInterval: restMileageInterval,
        };
      }
    }
  }

  return errors;
};

export const getReadonlyRules = (status?: string | null): ReadonlyRules => {
  if (!status) {
    return {
      isCompletelyReadonly: false,
      isPartiallyReadonly: false,
      readonlyFields: [],
    };
  }

  switch (status) {
    case REMINDER_STATUS_PROCESSED:
      return {
        isCompletelyReadonly: true,
        isPartiallyReadonly: false,
        readonlyFields: ['*'],
      };

    case REMINDER_STATUS_PENDING:
    case REMINDER_STATUS_PENDING_CONFIRM:
      return {
        isCompletelyReadonly: false,
        isPartiallyReadonly: true,
        readonlyFields: [
          'customerId',
          'type',
          // 'recipients.persons',
          'mileageInterval.vehicleId',
        ],
      };

    case REMINDER_STATUS_FAILED:
    default:
      return {
        isCompletelyReadonly: true,
        isPartiallyReadonly: false,
        readonlyFields: ['*'],
      };
  }
};

export const isFieldReadonly = (fieldName: string, readonlyRules: ReadonlyRules): boolean => {
  if (readonlyRules.isCompletelyReadonly) {
    return true;
  }

  if (readonlyRules.isPartiallyReadonly) {
    return (
      readonlyRules.readonlyFields.includes(fieldName) || readonlyRules.readonlyFields.includes('*')
    );
  }

  return false;
};

/**
 * Builds dateInterval payload based on form data
 */
export const buildDateInterval = (
  data: RemindersFormProps
): ReminderRequest['dateInterval'] | undefined => {
  const needsDateInterval = data.type === 'date' || data.type === 'dateAndMileage';

  if (!needsDateInterval) {
    return undefined;
  }

  // Convert Date to YYYY-MM-DD format for API (using local date, not UTC)
  const scheduleAtString = data.dateInterval?.scheduleAt
    ? formatDateToYYYYMMDD(data.dateInterval.scheduleAt)
    : undefined;

  // Build repeatInterval string: "5 day", "3 month", etc.
  let repeatInterval: string | undefined;
  if (data.dateInterval?.repeatInterval && data.recurrenceSettings?.period) {
    repeatInterval = `${data.dateInterval.repeatInterval} ${data.recurrenceSettings.period}`;
  }

  // Build notifyBeforeInterval string: "7 day", etc.
  // Only send notifyBeforeInterval when mode is not 'no-notify'
  let notifyBeforeInterval: string | undefined;
  const notifyMode = data.dateInterval?.notifyBeforeMode;

  if (notifyMode && notifyMode !== 'no-notify') {
    // For custom mode, use the notifyBeforeInterval value
    if (notifyMode === 'custom' && data.dateInterval?.notifyBeforeInterval !== undefined) {
      notifyBeforeInterval = `${data.dateInterval.notifyBeforeInterval} day`;
    }
    // For predefined modes (except 'no-notify'), the mode value IS the number of days
    else if (notifyMode !== 'custom') {
      notifyBeforeInterval = `${notifyMode} day`;
    }
  }

  return {
    scheduleAt: scheduleAtString,
    repeatInterval,
    notifyBeforeInterval,
  };
};

/**
 * Builds mileageInterval payload based on form data
 */
export const buildMileageInterval = (
  data: RemindersFormProps
): ReminderRequest['mileageInterval'] | undefined => {
  const needsMileageInterval = data.type === 'mileage' || data.type === 'dateAndMileage';

  if (!needsMileageInterval) {
    return undefined;
  }

  // Check if we have required mileage data
  if (!data.mileageInterval?.vehicleId) {
    return undefined;
  }

  // Build repeatInterval as number
  let repeatInterval: number | undefined;
  if (data.mileageInterval.repeatInterval) {
    repeatInterval = Number(data.mileageInterval.repeatInterval);
  }

  // Build notifyBeforeThreshold - only if value exists
  let notifyBeforeThreshold: number | undefined;
  if (
    data.mileageInterval.notifyBeforeThreshold !== undefined &&
    data.mileageInterval.notifyBeforeThreshold !== null
  ) {
    const numValue = Number(data.mileageInterval.notifyBeforeThreshold);
    // Only set if it's a valid number and greater than 0
    if (!Number.isNaN(numValue) && numValue > 0) {
      notifyBeforeThreshold = numValue;
    }
  }

  return {
    vehicleId: Number(data.mileageInterval.vehicleId),
    scheduleAt: Number(data.mileageInterval.scheduleAt),
    ...(notifyBeforeThreshold !== undefined && { notifyBeforeThreshold }),
    ...(repeatInterval !== undefined && { repeatInterval }),
  };
};

/**
 * Builds recipients payload based on form data
 */
export const buildRecipients = (data: RemindersFormProps): ReminderRequest['recipients'] => {
  // Build recipients object
  const recipients: ReminderRequest['recipients'] = {};

  // if allAssignedDrivers is true, set it and ignore persons
  if (data.recipients?.allAssignedDrivers) {
    recipients.allAssignedDrivers = true;
    // add emails array if exists
    if (data.recipients?.emails && data.recipients.emails.length > 0) {
      recipients.emails = data.recipients.emails;
    }
  } else if (data.recipients?.persons && data.recipients.persons.length > 0) {
    // Add users array if exists (only when allAssignedDrivers is not set)
    recipients.persons = data.recipients.persons.map((userId) => Number(userId));
  }

  return recipients;
};

/**
 * Builds filters payload based on form data
 */
export const buildFilters = (data: RemindersFormProps): ReminderRequest['filters'] | undefined => {
  if (!data.filters?.blacklistedUsers || data.filters.blacklistedUsers.length === 0) {
    return undefined;
  }

  return {
    blacklistedUsers: data.filters.blacklistedUsers.map((user) => Number(user)),
  };
};

/**
 * Builds complete ReminderRequest payload from form data
 */
export const buildReminderPayload = (data: RemindersFormProps): ReminderRequest => {
  return {
    type: data.type,
    // active: data.active,
    name: data.name,
    confirmable: data.confirmable,
    text: data.text,
    customerId: Number(data.customerId),
    recipients: buildRecipients(data),
    dateInterval: buildDateInterval(data),
    mileageInterval: buildMileageInterval(data),
    filters: buildFilters(data),
    notificationSettings: data.notificationSettings || [],
  };
};

export const prepareDuplicateData = (
  data: RemindersFormProps,
  f: IntlFormatters['formatMessage']
): RemindersFormProps => {
  return {
    ...data,
    name: `${data.name} ${f({ id: 'reminder.duplicate.suffix' })}`,
  };
};

export const getOptionLabel = (
  options: { value: string; item: string }[],
  value: string | number | undefined
): string => {
  const option = options.find((opt) => opt.value === String(value));
  return option?.item || '-';
};

/**
 * Format date to Slovak format (dd.MM.yyyy) without spaces
 * @param date - Date object or string in YYYY-MM-DD format or ISO format
 * @returns Formatted date in dd.MM.yyyy format (e.g., "31.01.2026")
 */
export const formatDate = (date: Date | string | undefined): string => {
  if (!date) return '-';
  try {
    const dateObj = date instanceof Date ? date : new Date(date);

    if (Number.isNaN(dateObj.getTime())) {
      return typeof date === 'string' ? date : '-';
    }

    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();

    return `${day}.${month}.${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return typeof date === 'string' ? date : '-';
  }
};

/**
 * Format date to Slovak narrative format (dd. MM. yyyy)
 * @param date - Date object
 * @returns Formatted date in "dd. MM. yyyy" format (e.g., "19. 12. 2026")
 */
export const formatDateLongSlovak = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}. ${month}. ${year}`;
};

/**
 * Format days with proper translation key for pluralization
 * @param days - Number of days
 * @param formatMessage - IntlFormatters formatMessage function
 * @returns Formatted string like "1 deň", "2 dni", "5 dní"
 */
export const formatDays = (
  days: number | undefined | null,
  formatMessage: IntlFormatters['formatMessage']
): string => {
  if (days === undefined || days === null) return '';
  return formatMessage(
    { id: 'reminder.field.dateInterval.notifyBeforeInterval.days' },
    { count: days }
  );
};

/**
 * Format number with thousands separator
 */
export const formatNumber = (value: number | string | undefined): string => {
  if (value === undefined || value === null || value === '') return '-';
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  return numValue.toLocaleString('sk-SK');
};

/**
 * Calculate notification date based on schedule date and notify before interval
 */
export const calculateNotificationDate = (
  scheduleAt: Date | string | undefined,
  notifyBeforeInterval: number | undefined
): Date | null => {
  if (!scheduleAt || !notifyBeforeInterval || notifyBeforeInterval === 0) {
    return null;
  }

  try {
    const scheduleDateObj = scheduleAt instanceof Date ? scheduleAt : new Date(scheduleAt);
    const daysToSubtract = notifyBeforeInterval;

    // Subtract days from schedule date
    const notificationDate = new Date(scheduleDateObj);
    notificationDate.setDate(notificationDate.getDate() - daysToSubtract);

    return notificationDate;
  } catch (error) {
    console.error('Error calculating notification date:', error);
    return null;
  }
};

/**
 * Calculate notification mileage based on schedule mileage and notify before threshold
 */
export const calculateNotificationMileage = (
  scheduleAt: number | undefined,
  notifyBeforeThreshold: number | undefined
): number | null => {
  if (!scheduleAt || !notifyBeforeThreshold || notifyBeforeThreshold === 0) {
    return null;
  }

  const notificationMileage = scheduleAt - notifyBeforeThreshold;
  return notificationMileage > 0 ? notificationMileage : null;
};

/**
 * Generate reminder notification info text
 * Returns formatted text showing when the reminder will be displayed
 * @param formData - Form data with notification settings
 * @param f - IntlFormatMessage function for translations
 * @returns Formatted notification info text or null if no notification configured
 */
export const getReminderNotificationInfo = (
  formData: RemindersFormProps,
  f: IntlFormatters['formatMessage']
): string | null => {
  const hasDateNotify =
    formData.dateInterval?.notifyBeforeMode &&
    formData.dateInterval.notifyBeforeMode !== 'no-notify' &&
    formData.dateInterval?.notifyBeforeInterval !== undefined;

  const hasMileageNotify =
    formData.mileageInterval?.notifyBeforeThreshold !== undefined &&
    formData.mileageInterval.notifyBeforeThreshold > 0;

  // No notification configured
  if (!hasDateNotify && !hasMileageNotify) {
    return null;
  }

  // Date notification part
  if (hasDateNotify && !hasMileageNotify) {
    const notificationDate = calculateNotificationDate(
      formData.dateInterval?.scheduleAt,
      formData.dateInterval?.notifyBeforeInterval
    );

    if (notificationDate) {
      const formattedDate = formatDateLongSlovak(notificationDate);
      return f({ id: 'reminder.notification.dateOnly' }, { date: formattedDate });
    }
  }

  // Mileage notification part only
  if (hasMileageNotify && !hasDateNotify) {
    const notificationMileage = calculateNotificationMileage(
      formData.mileageInterval?.scheduleAt,
      formData.mileageInterval?.notifyBeforeThreshold
    );

    if (notificationMileage !== null) {
      const formattedMileage = formatNumber(notificationMileage);
      return f({ id: 'reminder.notification.mileageOnly' }, { mileage: formattedMileage });
    }
  }

  // Both date and mileage
  if (hasDateNotify && hasMileageNotify) {
    const notificationDate = calculateNotificationDate(
      formData.dateInterval?.scheduleAt,
      formData.dateInterval?.notifyBeforeInterval
    );

    const notificationMileage = calculateNotificationMileage(
      formData.mileageInterval?.scheduleAt,
      formData.mileageInterval?.notifyBeforeThreshold
    );

    if (notificationDate && notificationMileage !== null) {
      const formattedDate = formatDateLongSlovak(notificationDate);
      const formattedMileage = formatNumber(notificationMileage);

      return f(
        { id: 'reminder.notification.dateAndMileage' },
        { date: formattedDate, mileage: formattedMileage }
      );
    }
  }

  return null;
};

/**
 * Add period to date
 */
export const addPeriodToDate = (date: Date, period: string, interval: number): Date => {
  const newDate = new Date(date);

  switch (period) {
    case 'day':
      newDate.setDate(newDate.getDate() + interval);
      break;
    case 'month':
      newDate.setMonth(newDate.getMonth() + interval);
      break;
    case 'year':
      newDate.setFullYear(newDate.getFullYear() + interval);
      break;
    default:
      // Unknown period, return date unchanged
      console.warn(`Unknown period type: ${period}`);
      break;
  }

  return newDate;
};

/**
 * Get period text with translation key for ICU format
 */
export const getPeriodTranslationKey = (
  period: string | undefined
): { key: string; periodType: string } | null => {
  if (!period) return null;

  switch (period) {
    case 'day':
      return { key: 'reminder.recurrence.period.day', periodType: 'day' };
    case 'month':
      return { key: 'reminder.recurrence.period.month', periodType: 'month' };
    case 'year':
      return { key: 'reminder.recurrence.period.year', periodType: 'year' };
    default:
      return null;
  }
};

/**
 * Generate recurrence notification info text
 * @param formData - Form data with recurrence settings
 * @param f - IntlFormatMessage function for translations
 * @returns Formatted recurrence info text or null if recurrence not enabled
 */
export const getRecurrenceNotificationInfo = (
  formData: RemindersFormProps,
  f: IntlFormatters['formatMessage']
): string | null => {
  const isDateRecurrenceEnabled =
    formData.dateInterval?.repeatMode && formData.dateInterval.repeatMode !== 'no-repeat';
  const isMileageRecurrenceEnabled =
    formData.mileageInterval?.repeatMode && formData.mileageInterval.repeatMode !== 'no-repeat';

  if (!isDateRecurrenceEnabled && !isMileageRecurrenceEnabled) {
    return null;
  }

  const hasDateInterval = formData.type === 'date' || formData.type === 'dateAndMileage';
  const hasMileageInterval = formData.type === 'mileage' || formData.type === 'dateAndMileage';

  // Date interval only
  if (hasDateInterval && !hasMileageInterval && isDateRecurrenceEnabled) {
    if (
      !formData.dateInterval?.scheduleAt ||
      !formData.dateInterval?.repeatInterval ||
      !formData.recurrenceSettings?.period
    ) {
      return null;
    }

    try {
      const scheduleDate = new Date(formData.dateInterval.scheduleAt);
      const repeatIntervalNumber = Number(formData.dateInterval.repeatInterval);

      if (Number.isNaN(scheduleDate.getTime()) || Number.isNaN(repeatIntervalNumber)) {
        return null;
      }

      // First notification date (with notifyBeforeInterval if set)
      const firstNotificationDate = new Date(scheduleDate);
      if (
        formData.dateInterval.notifyBeforeMode &&
        formData.dateInterval.notifyBeforeMode !== 'no-notify' &&
        formData.dateInterval.notifyBeforeInterval !== undefined
      ) {
        const notifyBeforeDays = Number(formData.dateInterval.notifyBeforeInterval);
        if (!Number.isNaN(notifyBeforeDays)) {
          firstNotificationDate.setDate(firstNotificationDate.getDate() - notifyBeforeDays);
        }
      }

      const formattedFirstDate = formatDateLongSlovak(firstNotificationDate);

      // Calculate next schedule date (scheduleAt + repeatInterval)
      const nextScheduleDate = addPeriodToDate(
        scheduleDate,
        formData.recurrenceSettings.period,
        repeatIntervalNumber
      );

      // Then subtract notifyBeforeInterval if set for next date
      const nextNotificationDate = new Date(nextScheduleDate);
      if (
        formData.dateInterval.notifyBeforeMode &&
        formData.dateInterval.notifyBeforeMode !== 'no-notify' &&
        formData.dateInterval.notifyBeforeInterval !== undefined
      ) {
        const notifyBeforeDays = Number(formData.dateInterval.notifyBeforeInterval);
        if (!Number.isNaN(notifyBeforeDays)) {
          nextNotificationDate.setDate(nextNotificationDate.getDate() - notifyBeforeDays);
        }
      }

      const formattedNextDate = formatDateLongSlovak(nextNotificationDate);

      const periodInfo = getPeriodTranslationKey(formData.recurrenceSettings.period);
      if (!periodInfo) return null;

      return f(
        { id: 'reminder.recurrence.dateOnly' },
        {
          firstDate: formattedFirstDate,
          nextDate: formattedNextDate,
          interval: repeatIntervalNumber,
          period: f({ id: periodInfo.key }, { count: repeatIntervalNumber }),
        }
      );
    } catch (error) {
      console.error('Error calculating recurrence date info:', error);
      return null;
    }
  }

  // Mileage interval only
  if (hasMileageInterval && !hasDateInterval && isMileageRecurrenceEnabled) {
    if (!formData.mileageInterval?.scheduleAt || !formData.mileageInterval?.repeatInterval) {
      return null;
    }

    const scheduleAtNumber = Number(formData.mileageInterval.scheduleAt);
    const repeatIntervalNumber = Number(formData.mileageInterval.repeatInterval);

    if (Number.isNaN(scheduleAtNumber) || Number.isNaN(repeatIntervalNumber)) {
      return null;
    }

    // First notification mileage (with notifyBeforeThreshold if set)
    let firstNotificationMileage = scheduleAtNumber;
    if (
      formData.mileageInterval.notifyBeforeThreshold !== undefined &&
      formData.mileageInterval.notifyBeforeThreshold > 0
    ) {
      const notifyBeforeThresholdNumber = Number(formData.mileageInterval.notifyBeforeThreshold);
      if (!Number.isNaN(notifyBeforeThresholdNumber)) {
        firstNotificationMileage -= notifyBeforeThresholdNumber;
      }
    }

    const formattedFirstMileage = formatNumber(firstNotificationMileage);

    // Calculate next schedule mileage (scheduleAt + repeatInterval)
    const nextScheduleMileage = scheduleAtNumber + repeatIntervalNumber;

    // Then subtract notifyBeforeThreshold if set for next mileage
    let nextNotificationMileage = nextScheduleMileage;
    if (
      formData.mileageInterval.notifyBeforeThreshold !== undefined &&
      formData.mileageInterval.notifyBeforeThreshold > 0
    ) {
      const notifyBeforeThresholdNumber = Number(formData.mileageInterval.notifyBeforeThreshold);
      if (!Number.isNaN(notifyBeforeThresholdNumber)) {
        nextNotificationMileage -= notifyBeforeThresholdNumber;
      }
    }

    const formattedNextMileage = formatNumber(nextNotificationMileage);
    const formattedRepeatInterval = formatNumber(repeatIntervalNumber);

    return f(
      { id: 'reminder.recurrence.mileageOnly' },
      {
        firstMileage: formattedFirstMileage,
        nextMileage: formattedNextMileage,
        repeatInterval: formattedRepeatInterval,
      }
    );
  }

  // Both date and mileage
  if (
    hasDateInterval &&
    hasMileageInterval &&
    (isDateRecurrenceEnabled || isMileageRecurrenceEnabled)
  ) {
    let formattedFirstDate: string | null = null;
    let formattedNextDate: string | null = null;
    let periodText: string | null = null;
    let dateIntervalNumber: number | null = null;

    // Calculate date part
    if (
      isDateRecurrenceEnabled &&
      formData.dateInterval?.scheduleAt &&
      formData.dateInterval?.repeatInterval &&
      formData.recurrenceSettings?.period
    ) {
      try {
        const scheduleDate = new Date(formData.dateInterval.scheduleAt);
        const repeatIntervalNumber = Number(formData.dateInterval.repeatInterval);

        if (!Number.isNaN(scheduleDate.getTime()) && !Number.isNaN(repeatIntervalNumber)) {
          // First notification date (with notifyBeforeInterval if set)
          const firstNotificationDate = new Date(scheduleDate);
          if (
            formData.dateInterval.notifyBeforeMode &&
            formData.dateInterval.notifyBeforeMode !== 'no-notify' &&
            formData.dateInterval.notifyBeforeInterval !== undefined
          ) {
            const notifyBeforeDays = Number(formData.dateInterval.notifyBeforeInterval);
            if (!Number.isNaN(notifyBeforeDays)) {
              firstNotificationDate.setDate(firstNotificationDate.getDate() - notifyBeforeDays);
            }
          }

          formattedFirstDate = formatDateLongSlovak(firstNotificationDate);

          // Calculate next schedule date (scheduleAt + repeatInterval)
          const nextScheduleDate = addPeriodToDate(
            scheduleDate,
            formData.recurrenceSettings.period,
            repeatIntervalNumber
          );

          // Then subtract notifyBeforeInterval if set for next date
          const nextNotificationDate = new Date(nextScheduleDate);
          if (
            formData.dateInterval.notifyBeforeMode &&
            formData.dateInterval.notifyBeforeMode !== 'no-notify' &&
            formData.dateInterval.notifyBeforeInterval !== undefined
          ) {
            const notifyBeforeDays = Number(formData.dateInterval.notifyBeforeInterval);
            if (!Number.isNaN(notifyBeforeDays)) {
              nextNotificationDate.setDate(nextNotificationDate.getDate() - notifyBeforeDays);
            }
          }

          formattedNextDate = formatDateLongSlovak(nextNotificationDate);

          const periodInfo = getPeriodTranslationKey(formData.recurrenceSettings.period);
          if (periodInfo) {
            periodText = f({ id: periodInfo.key }, { count: repeatIntervalNumber });
            dateIntervalNumber = repeatIntervalNumber;
          }
        }
      } catch (error) {
        console.error('Error calculating recurrence date info:', error);
      }
    }

    // Calculate mileage part
    let formattedFirstMileage: string | null = null;
    let formattedNextMileage: string | null = null;
    let formattedRepeatInterval: string | null = null;

    if (
      isMileageRecurrenceEnabled &&
      formData.mileageInterval?.scheduleAt &&
      formData.mileageInterval?.repeatInterval
    ) {
      const scheduleAtNumber = Number(formData.mileageInterval.scheduleAt);
      const repeatIntervalNumber = Number(formData.mileageInterval.repeatInterval);

      if (!Number.isNaN(scheduleAtNumber) && !Number.isNaN(repeatIntervalNumber)) {
        // First notification mileage (with notifyBeforeThreshold if set)
        let firstNotificationMileage = scheduleAtNumber;
        if (
          formData.mileageInterval.notifyBeforeThreshold !== undefined &&
          formData.mileageInterval.notifyBeforeThreshold > 0
        ) {
          const notifyBeforeThresholdNumber = Number(
            formData.mileageInterval.notifyBeforeThreshold
          );
          if (!Number.isNaN(notifyBeforeThresholdNumber)) {
            firstNotificationMileage -= notifyBeforeThresholdNumber;
          }
        }

        formattedFirstMileage = formatNumber(firstNotificationMileage);

        // Calculate next schedule mileage (scheduleAt + repeatInterval)
        const nextScheduleMileage = scheduleAtNumber + repeatIntervalNumber;

        // Then subtract notifyBeforeThreshold if set for next mileage
        let nextNotificationMileage = nextScheduleMileage;
        if (
          formData.mileageInterval.notifyBeforeThreshold !== undefined &&
          formData.mileageInterval.notifyBeforeThreshold > 0
        ) {
          const notifyBeforeThresholdNumber = Number(
            formData.mileageInterval.notifyBeforeThreshold
          );
          if (!Number.isNaN(notifyBeforeThresholdNumber)) {
            nextNotificationMileage -= notifyBeforeThresholdNumber;
          }
        }

        formattedNextMileage = formatNumber(nextNotificationMileage);
        formattedRepeatInterval = formatNumber(repeatIntervalNumber);
      }
    }

    // Build combined message
    if (
      formattedFirstDate &&
      formattedFirstMileage &&
      formattedNextDate &&
      formattedNextMileage &&
      periodText &&
      formattedRepeatInterval &&
      dateIntervalNumber !== null
    ) {
      return f(
        { id: 'reminder.recurrence.dateAndMileage' },
        {
          firstDate: formattedFirstDate,
          firstMileage: formattedFirstMileage,
          nextDate: formattedNextDate,
          nextMileage: formattedNextMileage,
          period: periodText,
          repeatInterval: formattedRepeatInterval,
          interval: dateIntervalNumber,
        }
      );
    }
  }

  return null;
};

/**
 * Get reminder status message for edit mode
 */
export const getReminderStatusMessage = (
  status: string | null,
  formatMessage: (descriptor: { id: string }) => string
): { text: string; boldText: string } | null => {
  if (!status) return null;

  switch (status) {
    case REMINDER_STATUS_PENDING_CONFIRM:
      return {
        text: formatMessage({ id: 'reminder.status.pendingConfirm.message.text' }),
        boldText: formatMessage({ id: 'reminder.status.pendingConfirm.message.bold' }),
      };
    case REMINDER_STATUS_PENDING:
      return {
        text: formatMessage({ id: 'reminder.status.pending.message.text' }),
        boldText: formatMessage({ id: 'reminder.status.pending.message.bold' }),
      };
    case REMINDER_STATUS_PROCESSED:
      return {
        text: formatMessage({ id: 'reminder.status.processed.message.text' }),
        boldText: formatMessage({ id: 'reminder.status.processed.message.bold' }),
      };
    case REMINDER_STATUS_FAILED:
      return {
        text: formatMessage({ id: 'reminder.status.failed.message.text' }),
        boldText: formatMessage({ id: 'reminder.status.failed.message.bold' }),
      };
    default:
      return null;
  }
};

/**
 * Get reminder status background color class
 */
export const getReminderStatusColorClass = (status: string | null): string => {
  switch (status) {
    case REMINDER_STATUS_PENDING_CONFIRM:
      return 'bg-warning-bg-subtle outline-warning-strong';
    case REMINDER_STATUS_PENDING:
      return 'bg-success-bg-subtle outline-success-default';
    case REMINDER_STATUS_PROCESSED:
      return 'bg-bg-darker outline-text-primary';
    case REMINDER_STATUS_FAILED:
      return 'bg-error-bg outline-brand-primary';
    default:
      return 'bg-info-bg outline-info-strong';
  }
};

export const getReminderNotificationType = (
  status: string | null
): 'success' | 'error' | 'warning' | 'info' => {
  switch (status) {
    case REMINDER_STATUS_PROCESSED:
      return 'info';
    case REMINDER_STATUS_PENDING:
      return 'success';
    case REMINDER_STATUS_PENDING_CONFIRM:
      return 'warning';
    case REMINDER_STATUS_FAILED:
      return 'error';
    default:
      return 'info';
  }
};

/**
 * Returns icon class name based on reminder status
 */
export const getReminderStatusIcon = (status: string | null): string => {
  switch (status) {
    case REMINDER_STATUS_PENDING_CONFIRM:
      return 'cmd-icon-exclamation';
    case REMINDER_STATUS_PENDING:
      return 'cmd-icon-active';
    case REMINDER_STATUS_PROCESSED:
      return 'cmd-icon-inactive';
    case REMINDER_STATUS_FAILED:
      return 'cmd-icon-error';
    default:
      return 'cmd-icon-exclamation';
  }
};

/**
 * Format date to YYYY-MM-DD format for API
 * Handles various input formats (dd.MM.yyyy, ISO, Date object)
 */
export const formatDateForAPI = (dateInput: string | Date | undefined): string | undefined => {
  if (!dateInput) return undefined;

  try {
    const dateObj = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

    if (Number.isNaN(dateObj.getTime())) {
      console.error('Invalid date:', dateInput);
      return undefined;
    }

    return formatDateToYYYYMMDD(dateObj);
  } catch (error) {
    console.error('Error formatting date for API:', error);
    return undefined;
  }
};

/**
 * Get comprehensive criteria notification info
 * Shows notification info regardless of recurrence, and adds recurrence details if enabled
 */
export const getCriteriaNotificationInfo = (
  formData: RemindersFormProps,
  f: IntlFormatters['formatMessage']
): string | null => {
  // First try to get recurrence info (which includes notification info)
  const recurrenceInfo = getRecurrenceNotificationInfo(formData, f);
  if (recurrenceInfo) {
    return recurrenceInfo;
  }

  // Try to get simple notification info (when notifyBefore is set)
  const notificationInfo = getReminderNotificationInfo(formData, f);
  if (notificationInfo) {
    return notificationInfo;
  }

  // If no recurrence and no notifyBefore, show basic schedule info using same translation keys
  const hasDateSchedule = !!formData.dateInterval?.scheduleAt;
  const hasMileageSchedule =
    formData.mileageInterval?.scheduleAt && formData.mileageInterval.scheduleAt > 0;

  const hasDateInterval = formData.type === 'date' || formData.type === 'dateAndMileage';
  const hasMileageInterval = formData.type === 'mileage' || formData.type === 'dateAndMileage';

  // Date only - just use scheduleAt without subtracting notifyBefore
  if (hasDateInterval && !hasMileageInterval && hasDateSchedule) {
    try {
      const scheduleDate = new Date(formData.dateInterval!.scheduleAt);
      if (!Number.isNaN(scheduleDate.getTime())) {
        const formattedDate = formatDateLongSlovak(scheduleDate);
        return f({ id: 'reminder.notification.dateOnly' }, { date: formattedDate });
      }
    } catch (error) {
      console.error('Error formatting schedule date:', error);
    }
  }

  // Mileage only - just use scheduleAt without subtracting notifyBefore
  if (hasMileageInterval && !hasDateInterval && hasMileageSchedule) {
    const formattedMileage = formatNumber(formData.mileageInterval!.scheduleAt);
    return f({ id: 'reminder.notification.mileageOnly' }, { mileage: formattedMileage });
  }

  // Both date and mileage - use scheduleAt values directly
  if (hasDateInterval && hasMileageInterval && (hasDateSchedule || hasMileageSchedule)) {
    if (hasDateSchedule && hasMileageSchedule) {
      try {
        const scheduleDate = new Date(formData.dateInterval!.scheduleAt);
        if (!Number.isNaN(scheduleDate.getTime())) {
          const formattedDate = formatDateLongSlovak(scheduleDate);
          const formattedMileage = formatNumber(formData.mileageInterval!.scheduleAt);
          return f(
            { id: 'reminder.notification.dateAndMileage' },
            { date: formattedDate, mileage: formattedMileage }
          );
        }
      } catch (error) {
        console.error('Error formatting schedule info:', error);
      }
    }
  }

  return null;
};

/**
 * Get vehicle licence number from vehicles dictionary
 * @param vehicles - Dictionary of vehicles
 * @param vehicleId - Vehicle ID to lookup
 * @returns Licence number or '-' if not found
 */
export const getVehicleLicenceNumber = (
  vehicles: IVehicles,
  vehicleId: number | undefined
): string => {
  if (!vehicleId) return '-';

  const vehicle = vehicles[vehicleId];
  if (!vehicle) return '-';

  return vehicle.licenceNumber || '-';
};

export const activate = async (reminderId: number | string): Promise<any> => {
  const response: AxiosResponse | false = await patchRequest(
    `/v1/reminders/${reminderId}/activate`,
    {},
    {
      returnAxiosResponse: true,
    }
  );
  if (response && response.status === 200 && response.data) {
    return response.data;
  }
  return null;
};

export const deactivate = async (reminderId: number | string): Promise<any> => {
  const response: AxiosResponse | false = await patchRequest(
    `/v1/reminders/${reminderId}/deactivate`,
    {},
    { returnAxiosResponse: true }
  );
  if (response && response.status === 200 && response.data) {
    return response.data;
  }
  return null;
};

export const cancelReminder = async (reminderId: number | string): Promise<any> => {
  const response: AxiosResponse | false = await deleteRequest(
    `/v1/reminders/${reminderId}/cancel`,
    { returnAxiosResponse: true }
  );
  if (response && response.status === 200 && response.data) {
    return response.data;
  }
  return null;
};
